import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { UserState } from '@store/root-store/auth/auth.reducer';
import { SnackbarService } from '@services/snackbar.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, first, mergeMap, tap } from 'rxjs/operators';
import { codeVersion } from './../utils/global-config';
import { AuthFacadeService } from '@store/root-store/auth/auth-facade.service';
import { LoaderFacadeService } from '@store/root-store/loader/loader-facade.service';

enum CustomEndpoints {
  Ongoing = '/ongoing',
  HistoryUpper = '/history/upper',
}

enum SnackBarStatus {
  INFO = 255,
  SUCCESS = 256,
  WARNING = 455,
  DANGER = 456,
  VERSION_MISMATCH = 426,
}

enum RedirectStatus {
  WRONG_TIMESTAMP = 412,
}

const BACKGROUND_REQUESTS = ['/firebase'];

const EXCLUDED_FROM_GLOBAL_LOADER = [
  '/login',
  '/notifications',
  '/userSettings',
  '/superEdit/checkShrink',
  '/drivers/logs/daily',
  '/speeding/settings',
  '/reports/vehicle/stops',
  '/reports/gps/vehicle/stops',
  '/fleetManagement/activities',
  '/reports/vehicle/historical-location',
  '/reports/gps/vehicle/historical-location',
  '/reports/gps/vehicle/eventDetails',
  '/reports/speeding/offenders',
  '/reports/speeding/summary',
];

@Injectable({
  providedIn: 'root',
})
export class JwtInterceptor implements HttpInterceptor {
  snackbarCodes = [SnackBarStatus.INFO, SnackBarStatus.SUCCESS, SnackBarStatus.WARNING, SnackBarStatus.DANGER];

  firstArrayHasItemOfSecondArray(arr1, arr2) {
    return arr1.some((r) => arr2.indexOf(r) >= 0);
  }

  constructor(
    private snackBar: SnackbarService,
    private router: Router,
    private authFacade: AuthFacadeService,
    private loaderFacade: LoaderFacadeService,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.authFacade.state$.pipe(
      first(),
      mergeMap((userState: UserState) => {
        request = this.handleRequest(request, userState);
        return next.handle(request).pipe(
          tap((response) => this.handleResponseSuccess(request, response)),
          catchError((error: HttpErrorResponse) => this.handleResponseError(request, error)),
        );
      }),
    );
  }

  handleRequest(request: HttpRequest<any>, userState) {
    const userTimestamp = String(Date.now());

    if (this.isGlobalLoader(request) && !this.isBackgroundRequest(request)) {
      this.loaderFacade.showGlobalLoader();
    }

    if (!request.url.startsWith('http')) {
      const url = `${environment.apiUrl}${request.url}`;
      request = request.clone({
        url,
        setHeaders: { userTimestamp, codeVersion },
      });
      const token = userState.isAuthenticated ? userState.user.token : false;

      if (token) {
        const Authorization = `Bearer ${token}`;
        request = request.clone({ setHeaders: { Authorization } });
      }
    }

    const params = request.params.keys();
    const hugeParamList = ['companyIds', 'groupIds', 'driverIds', 'vehicleIds', 'fmIds', 'trailerIds', 'geofenceIds'];

    const hasHugeParam = this.firstArrayHasItemOfSecondArray(params, hugeParamList);

    const isGetRequest = request.method === 'GET';

    if (isGetRequest && hasHugeParam) {
      const originalBody = request.body || {};
      const body: any = { ...originalBody };
      const companyIds = request.params.get('companyIds');
      body.companyIds = companyIds ? companyIds.split(',') : undefined;
      const groupIds = request.params.get('groupIds');
      body.groupIds = groupIds ? groupIds.split(',') : undefined;
      const vehicleIds = request.params.get('vehicleIds');
      body.vehicleIds = vehicleIds != null ? vehicleIds.split(',') : undefined;
      const driverIds = request.params.get('driverIds');
      body.driverIds = driverIds != null ? driverIds.split(',') : undefined;
      const fmIds = request.params.get('fmIds');
      body.fmIds = fmIds ? fmIds.split(',') : undefined;
      const trailerIds = request.params.get('trailerIds');
      body.trailerIds = trailerIds != null ? trailerIds.split(',') : undefined;
      const geofenceIds = request.params.get('geofenceIds');
      body.geofenceIds = geofenceIds ? geofenceIds.split(',') : undefined;

      /* ongoing tab needs custom logic */
      if (request.url.endsWith(CustomEndpoints.Ongoing)) {
        if (fmIds === '') {
          body.fmIds = [''];
        }
        if (vehicleIds === '') {
          body.vehicleIds = [''];
        }
        if (trailerIds === '') {
          body.trailerIds = [''];
        }
        if (driverIds === '') {
          body.driverIds = [''];
        }
      }

      /* history tab needs custom logic */
      if (request.url.endsWith(CustomEndpoints.HistoryUpper)) {
        if (driverIds === '') {
          body.driverIds = [''];
        }
      }

      const method = 'POST';
      const fetch = 'true';
      let newParams = request.params;
      hugeParamList.forEach((key) => {
        newParams = newParams.delete(key);
      });
      request = request.clone({
        method,
        body,
        setHeaders: { fetch },
        params: newParams,
      });
    }
    return request;
  }

  handleResponseSuccess(request, response) {
    if (response instanceof HttpResponse) {
      if (this.isGlobalLoader(request)) {
        this.loaderFacade.hideGlobalLoader();
      }
      this.showInfo(response);
      if (response.status > 299 && response.status < 400) {
        throw Error(`Redirection detected! Status code: ${response.status}`);
      }
    }
  }

  handleResponseError(request, error): Observable<any> | Promise<any> {
    if (error.status === SnackBarStatus.VERSION_MISMATCH) {
      const userStorage = localStorage.getItem('user');
      const user: any = JSON.parse(userStorage);
      if (user && user.token && user.authorities) {
        location.reload();
        return of();
      }
    }

    if (this.isGlobalLoader(request)) {
      this.loaderFacade.hideGlobalLoader();
    }
    this.showInfo(error);

    const isBlobError =
      error instanceof HttpErrorResponse && error.error instanceof Blob && error.error.type === 'application/json';

    if (isBlobError) {
      return this.throwBlobError(error);
    }
    return throwError(error);
  }

  throwBlobError(err) {
    return new Promise<any>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: Event) => {
        try {
          const error = JSON.parse((e.target as any).result);
          const { headers, status, statusText, url } = err;

          const httpError = new HttpErrorResponse({
            error,
            headers,
            status,
            statusText,
            url,
          });

          this.showInfo(httpError);
          reject(httpError);
        } catch (e) {
          reject(err);
        }
      };
      reader.onerror = (e) => {
        reject(err);
      };
      reader.readAsText(err.error);
    });
  }

  isGlobalLoader(request: HttpRequest<any>) {
    const isPostOrPut = ['POST', 'PUT'].includes(request.method);
    const isExcluded = EXCLUDED_FROM_GLOBAL_LOADER.some((endpoint) => request.url.includes(endpoint));
    return isPostOrPut && !isExcluded;
  }

  showInfo(response: HttpResponse<any> | HttpErrorResponse) {
    if (!this.snackbarCodes.includes(response.status)) {
      this.checkForRedirects(response);
      return;
    }
    const message = this.getSnackBarMessage(response);
    if (!message) {
      return;
    }
    switch (response.status) {
      case SnackBarStatus.INFO:
        this.snackBar.info(message);
        break;
      case SnackBarStatus.SUCCESS:
        this.snackBar.success(message);
        break;
      case SnackBarStatus.WARNING:
        this.snackBar.warning(message);
        break;
      case SnackBarStatus.DANGER:
        this.snackBar.danger(message);
        break;
    }
  }

  checkForRedirects(response) {
    if (response.status === RedirectStatus.WRONG_TIMESTAMP) {
      this.router.navigateByUrl('/auth/access-denied');
    } else if (response?.status > 410 && response?.status < 470) {
      this.router.navigateByUrl('/auth/error');
    }
  }

  getSnackBarMessage(response: HttpResponse<any> | HttpErrorResponse) {
    try {
      return response instanceof HttpErrorResponse
        ? response.error.message
        : response instanceof HttpResponse
          ? response.body.meta.snackBarMessage
          : null;
    } catch (e) {
      return null;
    }
  }

  isBackgroundRequest(request) {
    return BACKGROUND_REQUESTS.indexOf(request.url) > -1;
  }
}
